import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';
import { MetricDetailsTab } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { getIndicatorClass, hashCode } from 'webapp/utils/number';

import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type DateRange from 'webapp/models-custom/date-range';
import MetricSetup from 'webapp/models-custom/metric-setup';

import type { ColoredMetricSetupData } from 'webapp/services/metric-details';

interface Args {
	metricSetup: MetricSetup;
	coloredMetricSetupData: ColoredMetricSetupData;
	dateRange: DateRange | null;
}

export default class GoalsInsightsAdditionalBoxesForecastComponent extends Component<Args> {
	@service('model-manager/forecasts') declare mmForecastsService: Service.ModelManager.Forecasts;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('event-manager') declare eventManagerService: Service.EventManager;

	@tracked isLoading = true;
	@tracked forecastData: APIData.MetricInsights.ForecastDataResponse | null = null;
	@tracked isFirstDayOfTheWeekError = false;
	@tracked validationErrorText = '';

	private paramHash = 0;

	private currentRequestId: string | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.requestInsightsForecastDataOnNext(false);

		this.eventManagerService.on(AppEvent.METRIC_INSIGHTS_METRIC_EDITED, this, () => {
			this.requestInsightsForecastDataOnNext(true);
		});
	}

	get forText() {
		const isSingleOutput = this.args.metricSetup.isSingleOutput;
		return (
			(isSingleOutput
				? ''
				: `<b style="color: ${this.args.coloredMetricSetupData.colorHex}">${this.getSelectionName()}</b>`) +
			(this.forecastData && !this.isLoading
				? (isSingleOutput ? '' : '&nbsp;for ') + this.forecastData.forecastLabel
				: '')
		);
	}

	get firstDayOfTheWeekErrorText() {
		return this.mmForecastsService.firstDayOfTheWeekErrorText;
	}

	get indicatorClass() {
		const changeValue = this.forecastData?.changeValue;

		return typeof changeValue === 'number'
			? getIndicatorClass(changeValue, this.args.metricSetup.metric?.changeInvert)
			: '';
	}

	get dateRangeFormulaId() {
		return this.args.dateRange?.formulaId ?? '';
	}

	get specificMetricSetup() {
		// if metricSetup is multi-output, this is the one that also adds dimension/unit from the data (@coloredMetricSetupData)
		const specificMetricSetup = new MetricSetup(
			this.args.metricSetup.metricSetupWithoutFiltersData!,
		);
		const attributeName = this.args.coloredMetricSetupData.attributeName;
		if (attributeName) {
			specificMetricSetup.setFiltersFromAttributeName(attributeName);
		}
		specificMetricSetup.unitName = this.args.coloredMetricSetupData.unitName || null;

		return specificMetricSetup;
	}

	get isError() {
		return this.isFirstDayOfTheWeekError || !!this.validationErrorText;
	}

	get errorText() {
		return this.isFirstDayOfTheWeekError
			? this.firstDayOfTheWeekErrorText
			: this.validationErrorText;
	}

	getSelectionName() {
		const metricSetup = this.specificMetricSetup;

		if (metricSetup.firstAttributeNameFromBasicFilters) {
			let title = metricSetup.firstAttributeNameFromBasicFilters;
			if (metricSetup.specificSelectedUnit) {
				title += ` / ${metricSetup.specificSelectedUnit}`;
			}
			return title;
		} else {
			return metricSetup.metric?.name || metricSetup.key;
		}
	}

	private setForecastData(data: {
		forecastData: APIData.MetricInsights.ForecastDataResponse | null;
		formulaId: string;
		isFirstDayOfTheWeekError?: boolean;
		validationErrorText?: string;
	}) {
		if (data.formulaId === this.dateRangeFormulaId) {
			this.forecastData = data.forecastData;
			this.isFirstDayOfTheWeekError = !!data.isFirstDayOfTheWeekError;
			this.validationErrorText = data.validationErrorText ?? '';
		}
	}

	private requestInsightsForecastData(forceNewRequest = false) {
		this.isLoading = true;
		const metricSetup = this.specificMetricSetup,
			dateRange = this.args.dateRange!, // We know dateRange is set, cause the metricSetup comes from chartData.
			formulaId = this.dateRangeFormulaId;

		if (!dateRange.isToday && !dateRange.isYesterday) {
			const newParamHash = hashCode(JSON.stringify(metricSetup.metricSetupData));

			if (forceNewRequest || this.paramHash !== newParamHash) {
				this.paramHash = newParamHash;

				// if request already in progress, cancel subscription
				if (this.currentRequestId) {
					this.mmForecastsService.unsubscribeFromInsightsForecastRequest(
						this.currentRequestId,
					);
				}

				const requestId = this.mmForecastsService.requestInsightsForecastData(
					dateRange,
					metricSetup,
					(forecastData) => {
						if (requestId !== this.currentRequestId) {
							return true; // unsubscribe, this is an outdated request
						}

						this.setForecastData({
							forecastData,
							formulaId,
						});
						this.isLoading = false;
						this.currentRequestId = null;
						return true; // unsubscribe after handling
					},
					(errorData) => {
						if (requestId !== this.currentRequestId) {
							return;
						}

						const isFirstDayOfTheWeekError =
							this.mmForecastsService.getIsTheFirstDayOfTheWeekError(errorData);
						const validationErrorText =
							this.mmForecastsService.getValidationErrorText(errorData);

						this.setForecastData({
							forecastData: null,
							formulaId,
							isFirstDayOfTheWeekError,
							validationErrorText,
						});

						this.isLoading = false;
						this.currentRequestId = null;
					},
				);

				this.currentRequestId = requestId;
			}
		} else {
			this.paramHash = 0;
			this.setForecastData({
				forecastData: null,
				formulaId,
			});
		}
	}

	@action
	requestInsightsForecastDataOnNext(forceNewRequest: boolean) {
		// Don't call requestInsightsForecastData directlyfrom did-update cause it passes the dom element (_el: HTMLElement), which is then wrongly evaluated as forceNewRequest being true.
		next(() => {
			this.requestInsightsForecastData(forceNewRequest);
		});
	}

	@action
	openExplorer() {
		const metricSetup = this.specificMetricSetup;

		if (metricSetup.metric) {
			this.metricDetailsService.openMetricDetailsDrawer(
				{
					metricSetup,
					tab: MetricDetailsTab.FORECAST,
				},
				'metric-insights',
			);
		}
	}

	@action
	teardownView(): void {
		// Use dom event, not willDestroy cause user might quickly navigate through visited routes
		this.eventManagerService.off(AppEvent.METRIC_INSIGHTS_METRIC_EDITED, this);
	}

	override willDestroy() {
		super.willDestroy();
		if (this.currentRequestId) {
			this.mmForecastsService.unsubscribeFromInsightsForecastRequest(this.currentRequestId);
		}
	}
	<template>
		<div
			class="module-box forecast"
			{{didUpdate (fn this.requestInsightsForecastDataOnNext false) @dateRange}}
			{{willDestroy this.teardownView}}
		>
			<div class="module-box__title has-explorer-link">
				<div class="flex-child-wrapper">
					<CommonUiIconComponent @name="Forecasts" @size="md" />
					<span class="for">
						Forecast{{#if this.forText}} for {{/if}}
					</span>
					{{{this.forText}}}
				</div>
				<a role="button" class="explorer-link" {{on "click" this.openExplorer}}>
					Details
					<CommonUiIconComponent @name="External" @size="sm" />
				</a>
			</div>

			{{#if this.isLoading}}
				<div class="module-box__loader">
					<BarLoaderComponent @size="tiny" />
					Preparing...
				</div>
			{{else if this.forecastData}}
				<div class="module-box__value">
					{{this.forecastData.forecastValueStr}}
				</div>

				{{#if this.forecastData.changeString}}
					<div class="module-box__footer">
						<span class="indicator {{this.indicatorClass}}">
							{{this.forecastData.changeString}}
						</span>
						vs previous period
					</div>
				{{/if}}
			{{else}}
				<div class="module-box__value no-data">Not available</div>

				{{#if this.isError}}
					<div class="error">
						<ToolTipWithIconComponent
							@text={{{this.errorText}}}
							@iconName="AlertFill"
							@maxWidth={{230}}
						/>
					</div>
				{{/if}}

			{{/if}}
		</div>
	</template>
}
